import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Activity, GitBranch, Eye, ShieldCheck, Layers } from "lucide-react";
import EquilibriumLayout from "@/components/equilibrium/EquilibriumLayout";
import { fetchEqModule } from "@/lib/eq";

const ICONS = {
  spyglass: Eye,
  atlasbridge: GitBranch,
  horizon: Activity,
  dutydeck: ShieldCheck,
  oli: Layers,
};

export default function ModulePage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [mod, setMod] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setMod(null);
    setErr(false);
    window.scrollTo({ top: 0 });
    fetchEqModule(key)
      .then(setMod)
      .catch(() => setErr(true));
  }, [key]);

  const Icon = ICONS[key] || Activity;

  return (
    <EquilibriumLayout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
        <button
          onClick={() => navigate("/equilibrium")}
          data-testid="eq-module-back"
          className="flex items-center gap-2 text-sm text-axiom-muted hover:text-axiom-text transition-colors mb-8"
        >
          <ArrowLeft size={14} /> All modules
        </button>

        {err && (
          <p className="text-axiom-muted" data-testid="eq-module-not-found">
            Module not found.
          </p>
        )}

        {mod && (
          <div data-testid={`eq-module-${mod.key}`}>
            {/* Header */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 border border-axiom-accent/50 flex items-center justify-center">
                    <Icon size={22} className="text-axiom-accent" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="num-label">{mod.domain} · {mod.version}</div>
                    <div className="font-display text-3xl sm:text-4xl lg:text-5xl text-axiom-text font-bold tracking-tight mt-1">
                      {mod.name}
                    </div>
                  </div>
                </div>
                <p className="font-display text-xl text-axiom-accent/90 leading-snug max-w-2xl">
                  {mod.tagline}
                </p>
                <p className="mt-5 text-axiom-muted text-base leading-relaxed max-w-2xl">
                  {mod.purpose}
                </p>
              </div>

              <div className="lg:col-span-5">
                <div className="border border-white/10 bg-axiom-surface/40 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="num-label">MODULE TELEMETRY</span>
                    <StatusPill status={mod.status} />
                  </div>
                  <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                    {[
                      { k: "CHANNELS", v: mod.telemetry.channels },
                      { k: "EVENTS/HR", v: mod.telemetry.events_per_hour.toLocaleString() },
                      { k: "COHERENCE", v: `${mod.telemetry.coherence_pct}%`, accent: true },
                    ].map((m) => (
                      <div key={m.k} className="bg-axiom-bg p-3">
                        <div className="num-label">{m.k}</div>
                        <div
                          className={`mt-1 font-display text-lg ${
                            m.accent ? "text-axiom-accent" : "text-axiom-text"
                          }`}
                        >
                          {m.v}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5">
                    <div className="num-label mb-2">INPUTS</div>
                    <div className="flex flex-wrap gap-2">
                      {mod.inputs.map((i) => (
                        <span
                          key={i}
                          className="text-[10px] font-mono uppercase tracking-wider border border-white/10 text-axiom-muted px-2 py-1"
                        >
                          {i}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Body grid */}
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
              <div className="bg-axiom-bg p-7">
                <div className="num-label mb-4">WHAT IT OBSERVES</div>
                <ul className="space-y-3">
                  {mod.observes.map((line) => (
                    <li key={line} className="flex items-start gap-3 text-sm text-axiom-text/90 leading-relaxed">
                      <span className="signal-dot mt-1.5" style={{ width: 5, height: 5 }} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-axiom-bg p-7">
                <div className="num-label mb-4">WHAT IT PRODUCES</div>
                <ul className="space-y-3">
                  {mod.outputs.map((line) => (
                    <li key={line} className="flex items-start gap-3 text-sm text-axiom-text/90 leading-relaxed">
                      <span className="signal-dot mt-1.5 bg-axiom-accent" style={{ width: 5, height: 5 }} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Architecture sketch */}
            <div className="mt-14">
              <div className="num-label mb-4">ARCHITECTURE</div>
              <div className="border border-white/10 bg-axiom-bg p-6 md:p-8">
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4 items-center">
                  {[
                    "INGESTION",
                    "NORMALIZATION",
                    `${mod.name.toUpperCase()} CORE`,
                    "PROVENANCE",
                    "DOWNSTREAM",
                  ].map((s, i, arr) => (
                    <div key={s} className="relative">
                      <div
                        className={`aspect-square border ${
                          i === 2
                            ? "border-axiom-accent bg-axiom-accent/5"
                            : "border-white/15"
                        } flex items-center justify-center p-2`}
                      >
                        <span className={`text-[10px] font-mono text-center leading-tight ${
                          i === 2 ? "text-axiom-accent" : "text-axiom-muted"
                        }`}>
                          {s}
                        </span>
                      </div>
                      {i < arr.length - 1 && (
                        <div className="hidden sm:block absolute top-1/2 -right-2 w-4 h-px bg-white/20" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Continue */}
            <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-8">
                <h3 className="font-display text-2xl text-axiom-text font-bold tracking-tight">
                  Provision a diagnostic.
                </h3>
                <p className="mt-2 text-axiom-muted text-sm leading-relaxed max-w-2xl">
                  Submit a structured intake and the platform will score confidence, estimate
                  entropy, and recommend whether {mod.name} is the right entry-point for your network.
                </p>
              </div>
              <div className="lg:col-span-4 flex justify-end">
                <button
                  onClick={() => navigate("/equilibrium/diagnostic")}
                  className="btn-axiom"
                >
                  Run Diagnostic <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </EquilibriumLayout>
  );
}

function StatusPill({ status }) {
  const map = {
    OPERATIONAL: "border-emerald-400/40 text-emerald-300",
    PROVISIONAL: "border-amber-400/40 text-amber-300",
    FOUNDATIONAL: "border-axiom-accent/40 text-axiom-accent",
  };
  return (
    <span
      className={`text-[10px] font-mono uppercase tracking-wider border px-2 py-0.5 ${
        map[status] || "border-white/15 text-axiom-muted"
      }`}
    >
      {status}
    </span>
  );
}
